import { Chip, Container, Typography } from '@cleartrip/ct-design-components'
import Image  from 'next/image'
import { FC, RefObject, useLayoutEffect, useRef } from 'react'

import { GreenTick } from '@/assets/icons/GreenTick'
import {
  PE_VALUE_MAX_BENEFITS_BACKGROUND,
  PE_VALUE_MAX_BENEFITS_CHIP_RADIUS,
  PE_VALUE_MAX_BENEFITS_RADIUS,
} from '@/constants/flights/premiumEconomy'

export interface BenifitSummaryProps {
  title?: string
  /** List of benefit labels for the tag chips */
  benefits?: {
    title?: string
    type?: string
  }[]
  /** Optional custom icon element; defaults to GreenTick when title is present */
  icon?: string
  className?: string
  benefitRef?: RefObject<HTMLDivElement>
  /**
   * Themed cabin treatment (Premium Economy / Business / First Class tabs of the
   * PE-discovery interstitial and itinerary modify-fare modal): cream panel with
   * white pill chips, per Figma 4143:78063. Economy keeps the untinted default.
   */
  isThemedCabin?: boolean
}

/** Benefit summary strip for CT branded fare cards: title with check icon and benefit chips. */
const BenifitSummary: FC<BenifitSummaryProps> = ({
  title = '',
  benefits = [],
  icon,
  className = '',
  benefitRef,
  isThemedCabin = false,
}) => {
  const BenifitSummaryRef = useRef<HTMLDivElement>(null)
  const showHeader = icon !== undefined || title
  const defaultIcon = <GreenTick height={16} width={16} />
  const benefitLabels = benefits?.map((benefit: any) => benefit.title) || []

  // Reserve space under the left benefit labels so they stay aligned with
  // fare-card rows when this Value Max strip is present. Callers clear
  // marginBottom when no branded card is visible (e.g. Premium Economy tab).
  // useLayoutEffect so the strip grows before paint (small → tall), not after.
  useLayoutEffect(() => {
    const labelEl = benefitRef?.current
    const summaryEl = BenifitSummaryRef.current
    if (!labelEl || !summaryEl) return

    labelEl.style.marginBottom = `${summaryEl.clientHeight}px`
  }, [benefitRef, title, benefitLabels.length, icon])
  if (!title && benefitLabels?.length === 0) return null
  return (
    <Container
      ref={BenifitSummaryRef}
      className={`flex flex-column flex-rg-1 p-2 ${isThemedCabin ? '' : 'br-16'} ${className}`.trim()}
      css={
        isThemedCabin
          ? {
              background: PE_VALUE_MAX_BENEFITS_BACKGROUND,
              // Full-bleed footer: it closes off the bottom of the card, so only the
              // bottom corners are rounded (Figma 4143:78021).
              borderRadius: `0 0 ${PE_VALUE_MAX_BENEFITS_RADIUS} ${PE_VALUE_MAX_BENEFITS_RADIUS}`,
            }
          : undefined
      }
    >
      {showHeader && (
        <Container className='flex flex-middle flex-cg-1'>
          {icon !== undefined ? <Image alt='icon' height={16} src={icon} width={16} /> : defaultIcon}
          <Typography className='fw-600' style={{paddingLeft: '2px'}} variant='B3'>
            {title}
          </Typography>
        </Container>
      )}
      {benefitLabels?.length > 0 && (
        <Container className='flex flex-wrap flex-gap-1'>
          {benefitLabels.map((benefit, index) => (
            <Chip
              key={`${benefit}-${index}`}
              label={
                <Typography className='ws-nowrap' variant='B4'>
                  {benefit}
                </Typography>
              }
              styleConfig={{
                root: {
                  css: {
                    padding: '2px 6px',
                    ...(isThemedCabin ? { borderRadius: PE_VALUE_MAX_BENEFITS_CHIP_RADIUS } : {}),
                  },
                  // Drop the br-12 utility when themed so the pill radius above wins
                  // regardless of stylesheet insertion order.
                  className: `bg-white${isThemedCabin ? '' : ' br-12'}`,
                },
              }}
            />
          ))}
        </Container>
      )}
    </Container>
  )
}

export default BenifitSummary
